import Big from 'big.js';
import isUUID from 'validator/es/lib/isUUID';
import isEmail from 'validator/lib/isEmail';

import { APIConfig } from 'src/config';
import { isFloatValueAndGTEQZero } from 'src/util/math';
import isDirectionValid from 'src/util/tradeDirection/validation';

import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from '../constants';

/**
 * Validates the provided proposer trade point ID against the UUID format.
 * @param {string} proposerTradePointId - The proposer trade point ID to validate.
 * @returns {boolean} - True if the provided ID is a valid UUID, false otherwise.
 */
export const isProposerTradePointIdValid = (proposerTradePointId) => (
  proposerTradePointId ? isUUID(proposerTradePointId) : false);

/**
 * Validates a trade rule by checking the following:
 * - The trade direction is valid
 * - The proposer trade point ID is a valid UUID
 * - The recipient user email is valid
 * - All clause prices are valid
 * @param {object} tradeRule - The trade rule to validate.
 * @returns {boolean} - True if the trade rule is valid, false otherwise.
 */
export const isTradeRuleValid = (tradeRule) => {
  if (!tradeRule) {
    return false;
  }
  return isDirectionValid(tradeRule.direction)
  && isProposerTradePointIdValid(tradeRule.proposerTradePointId)
  && isEmail(tradeRule.recipientUserEmail)
  && tradeRule.clauses.every(({ price }) => isFloatValueAndGTEQZero(price));
};

/**
 * Validates a buy price against community trading limits.
 * @param {string|number|Big} price - Price in cents per kWh
 * @returns {boolean} True if price is within valid buy price range
 */
export const isBuyPriceValid = (price) => {
  if (!price) {
    return false;
  }
  let floatPrice;
  if (typeof price === 'number' || typeof price === 'string') {
    floatPrice = Big(price);
  } else {
    floatPrice = price;
  }

  const convertedPrice = floatPrice.div(Big(1000.0).times(Big(100.0)));

  const { COMMUNITY_BUY_PRICE_MAX, COMMUNITY_BUY_PRICE_MIN } = APIConfig();

  if (COMMUNITY_BUY_PRICE_MIN !== undefined && convertedPrice < COMMUNITY_BUY_PRICE_MIN) {
    return false;
  }
  if (COMMUNITY_BUY_PRICE_MAX !== undefined && convertedPrice > COMMUNITY_BUY_PRICE_MAX) {
    return false;
  }

  return true;
};

/**
 * Validates a sell price against community trading limits.
 * @param {string|number|Big} price - Price in cents per kWh
 * @returns {boolean} True if price is within valid sell price range
 */
export const isSellPriceValid = (price) => {
  if (!price) {
    return false;
  }

  let floatPrice;
  if (typeof price === 'number' || typeof price === 'string') {
    floatPrice = Big(price);
  } else {
    floatPrice = price;
  }

  const convertedPrice = floatPrice.div(Big(1000.0).times(Big(100.0)));

  const { COMMUNITY_SELL_PRICE_MAX, COMMUNITY_SELL_PRICE_MIN } = APIConfig();

  if (COMMUNITY_SELL_PRICE_MIN !== undefined && convertedPrice < COMMUNITY_SELL_PRICE_MIN) {
    return false;
  }
  if (COMMUNITY_SELL_PRICE_MAX !== undefined && convertedPrice > COMMUNITY_SELL_PRICE_MAX) {
    return false;
  }

  return true;
};

/**
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL | string} tradeDirection
 * @returns {Function} whether the community price is valid.
 */
export const isCommunityPriceValid = (tradeDirection) => {
  switch (tradeDirection) {
    case TRADE_DIRECTION_BUY:
      return isBuyPriceValid;
    case TRADE_DIRECTION_SELL:
      return isSellPriceValid;
    default:
      return () => false;
  }
};
